/** Product proposals (0070): an Account Executive or a campaign offers a
 *  specific product; the client decides on screen. Mirrors
 *  backend/app/api/v1/routes/proposals.py. */

export type ProposalCadence = 'one_time' | 'weekly' | 'monthly';
export type ProposalOrigin = 'agent' | 'campaign';

export interface Proposal {
  id: string;
  product_id: string;
  product_name: string;
  /** Null whenever funds could not answer for this vehicle (0073). */
  asset_class: string | null;
  currency_code: string | null;
  min_investment: string | null;
  origin: ProposalOrigin;
  suggested_amount: string | null;
  suggested_cadence: ProposalCadence | null;
  message: string | null;
  expires_at: string;
  created_at: string;
  contract_template_version_id: string | null;
  /** True when the client already holds a contract for this product: accepting
   *  sends them to fund it instead of signing again. */
  has_active_contract: boolean;
  /** Account Executive's name for an agent proposal; null for a campaign. */
  proposed_by_name: string | null;
}

export interface CreateProposalBody {
  product_id: string;
  suggested_amount?: string | null;
  suggested_cadence?: ProposalCadence | null;
  message?: string | null;
  expires_in_days?: number;
}

export interface AcceptProposalResult {
  status: 'accepted';
  next_step: 'fund' | 'contract';
}

export interface CreateCampaignBody {
  product_id: string;
  name: string;
  client_ids: string[];
  message?: string | null;
  expires_in_days?: number;
}

export interface CampaignResult {
  campaign_id: string;
  target_count: number;
  created_count: number;
  skipped: { client_id: string; reason: string }[];
}

export interface Campaign {
  id: string;
  product_id: string;
  product_name: string;
  name: string;
  target_count: number;
  created_count: number;
  expires_at: string;
  created_at: string;
}

/** Why a campaign target got no proposal. Keep in lockstep with
 *  SKIP_* in backend/app/domains/finance/proposals.py. */
export const PROPOSAL_SKIP_LABEL: Record<string, string> = {
  not_a_client: 'Not a client account',
  inactive: 'Inactive account',
  on_hold: 'Under a compliance hold',
  unsuitable: 'Series not suitable for this client',
  already_open: 'Already has an open proposal for this series',
  closed_to_new_clients: 'Series closed to new clients',
};

export const CADENCE_LABEL: Record<ProposalCadence, string> = {
  monthly: 'monthly',
  weekly: 'weekly',
  one_time: 'one time',
};
